import posthog from 'posthog-js';
import GTM from './GTM';
import SnapchatPixel from './SnapchatPixel';
import { facebookCAPI } from './FacebookCAPI/api';
import * as amplitude from '@amplitude/analytics-browser';

/**
 * ANALYTICS EVENT ENUMS
 * Centralized event names for all analytics providers to ensure consistency
 */

// Facebook Pixel & CAPI Events
export const FACEBOOK_EVENTS = {
  PURCHASE: 'Purchase',
  ADD_TO_CART: 'AddToCart',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  LOGIN: 'Login',
  PAYMENT: 'Payment',
  PAYMENT_SUCCESS: 'PaymentSuccess',
  PAYMENT_FAILED: 'PaymentFailed',
  VIEW_CONTENT: 'ViewContent',
  CUSTOM_LOGIN: 'Login' // For trackCustom calls
};

// Google Analytics Events
export const GOOGLE_ANALYTICS_EVENTS = {
  PURCHASE: 'purchase',
  ADD_TO_CART: 'add_to_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  CONVERSION: 'conversion',
  PAGE_VIEW: 'page_view'
};

// Google Ads Conversion Labels
export const GOOGLE_ADS_CONVERSIONS = {
  PURCHASE: 'AW-10792886696/purchase-conversion-label',
  BEGIN_CHECKOUT: 'AW-10792886696/begin-checkout-conversion-label',
  ADD_TO_CART: 'AW-10792886696/add-to-cart-conversion-label'
};

// Snapchat Pixel Events
export const SNAPCHAT_EVENTS = {
  PURCHASE: 'PURCHASE',
  ADD_TO_CART: 'ADD_CART',
  START_CHECKOUT: 'START_CHECKOUT',
  VIEW_CONTENT: 'VIEW_CONTENT',
  LOGIN: 'LOGIN'
};

// ShareChat Analytics Events
export const SHARECHAT_EVENTS = {
  PURCHASE: 'Purchase',
  ADD_TO_CART: 'Add to cart'
};

// PostHog Events
export const POSTHOG_EVENTS = {
  PAGE_VIEW: '$pageview',
  PURCHASE_COMPLETE: 'purchase_complete',
  ADD_TO_CART: 'add_to_cart',
  ERROR_OCCURRED: 'error_occurred'
};

// Amplitude Events
export const AMPLITUDE_EVENTS = {
  CHECKOUT_PAGE_VIEWED: 'Checkout Page Viewed',
  PURCHASE_COMPLETED: 'Purchase Completed',
  PAYMENT_SUCCESS: 'Payment Success',
  PAYMENT_FAILED: 'Payment Failed',
  PAYMENT_PROCESSING: 'Payment Processing',
  LOGIN_SUCCESS: 'Login Success',
  SIGNUP_SUCCESS: 'Signup Success',
  ERROR_OCCURRED: 'Error Occurred'
};

// Payment Event Types
export const PAYMENT_EVENT_TYPES = {
  SUCCESS: 'success',
  FAILED: 'failed',
  PROCESSING: 'processing',
  INITIATED: 'initiated'
};

// Authentication Event Types
export const AUTH_EVENT_TYPES = {
  LOGIN: 'login',
  SIGNUP: 'signup',
  LOGOUT: 'logout'
};

// Analytics Provider Enums
export const ANALYTICS_PROVIDERS = {
  FACEBOOK_PIXEL: 'facebook_pixel',
  FACEBOOK_CAPI: 'facebook_capi',
  GOOGLE_ANALYTICS: 'google_analytics',
  GOOGLE_ADS: 'google_ads',
  SNAPCHAT: 'snapchat',
  SHARECHAT: 'sharechat',
  POSTHOG: 'posthog',
  AMPLITUDE: 'amplitude',
  GTM: 'gtm'
};

// Custom Event Names
export const CUSTOM_EVENTS = {
  // User Interaction Events
  BUTTON_CLICKED: 'Button Clicked',
  CTA_CLICKED: 'CTA Clicked',
  LINK_CLICKED: 'Link Clicked',
  MODAL_OPENED: 'Modal Opened',
  MODAL_CLOSED: 'Modal Closed',
  TAB_SWITCHED: 'Tab Switched',
  DROPDOWN_OPENED: 'Dropdown Opened',
  SEARCH_PERFORMED: 'Search Performed',
  FILTER_APPLIED: 'Filter Applied',
  SORT_CHANGED: 'Sort Changed',
  
  // Page/Content Events
  PAGE_SCROLL: 'Page Scroll',
  CONTENT_VIEWED: 'Content Viewed',
  VIDEO_PLAYED: 'Video Played',
  VIDEO_PAUSED: 'Video Paused',
  VIDEO_COMPLETED: 'Video Completed',
  IMAGE_CLICKED: 'Image Clicked',
  DOCUMENT_DOWNLOADED: 'Document Downloaded',
  EXTERNAL_LINK_CLICKED: 'External Link Clicked',
  
  // E-commerce Events
  PRODUCT_VIEWED: 'Product Viewed',
  PRODUCT_COMPARED: 'Product Compared',
  WISHLIST_ADDED: 'Wishlist Added',
  WISHLIST_REMOVED: 'Wishlist Removed',
  COUPON_APPLIED: 'Coupon Applied',
  COUPON_REMOVED: 'Coupon Removed',
  SHIPPING_INFO_ADDED: 'Shipping Info Added',
  PAYMENT_INFO_ADDED: 'Payment Info Added',
  PAYMENT_CONFIRMATION_VIEWED: 'Payment Confirmation Page Viewed',
  PAYMENT_INITIATED: 'Payment Initiated',
  PAYMENT_GATEWAY_REDIRECT: 'Payment Gateway Redirect',
  
  // User Engagement Events
  NEWSLETTER_SIGNUP: 'Newsletter Signup',
  SOCIAL_SHARE: 'Social Share',
  REVIEW_SUBMITTED: 'Review Submitted',
  RATING_GIVEN: 'Rating Given',
  FEEDBACK_SUBMITTED: 'Feedback Submitted',
  CONTACT_FORM_SUBMITTED: 'Contact Form Submitted',
  LIVE_CHAT_STARTED: 'Live Chat Started',
  HELP_ARTICLE_VIEWED: 'Help Article Viewed',
  
  // Feature Usage Events
  FEATURE_USED: 'Feature Used',
  TOOL_ACCESSED: 'Tool Accessed',
  SETTING_CHANGED: 'Setting Changed',
  PREFERENCE_UPDATED: 'Preference Updated',
  PROFILE_UPDATED: 'Profile Updated',
  NOTIFICATION_CLICKED: 'Notification Clicked',
  NOTIFICATION_DISMISSED: 'Notification Dismissed',
  
  // Marketing Events
  CAMPAIGN_VIEWED: 'Campaign Viewed',
  CAMPAIGN_CLICKED: 'Campaign Clicked',
  AD_IMPRESSION: 'Ad Impression',
  AD_CLICKED: 'Ad Clicked',
  PROMOTION_VIEWED: 'Promotion Viewed',
  BANNER_CLICKED: 'Banner Clicked',
  EMAIL_OPENED: 'Email Opened',
  EMAIL_CLICKED: 'Email Clicked',
  
  // Performance/Technical Events
  PAGE_LOAD_TIME: 'Page Load Time',
  API_RESPONSE_TIME: 'API Response Time',
  SEARCH_RESULTS_LOADED: 'Search Results Loaded',
  IMAGE_LOAD_ERROR: 'Image Load Error',
  FORM_VALIDATION_ERROR: 'Form Validation Error',
  NETWORK_ERROR: 'Network Error',
  ERROR_OCCURRED: 'Error Occurred',
  REACT_ERROR_BOUNDARY: 'React Error Boundary Triggered',
  
  // A/B Testing Events
  AB_TEST_IMPRESSION: 'A/B Test Impression',
  AB_TEST_CONVERSION: 'A/B Test Conversion',
  EXPERIMENT_VIEWED: 'Experiment Viewed',
  VARIANT_ASSIGNED: 'Variant Assigned',
  
  // Session Events
  SESSION_STARTED: 'Session Started',
  SESSION_EXTENDED: 'Session Extended',
  IDLE_TIMEOUT: 'Idle Timeout',
  TAB_VISIBILITY_CHANGED: 'Tab Visibility Changed',
  BROWSER_BACK_BUTTON: 'Browser Back Button',
  
  // Custom Business Events (Fleek-specific)
  BUNDLE_EXPLORED: 'Bundle Explored',
  PLAN_COMPARED: 'Plan Compared',
  SUBSCRIPTION_PAUSED: 'Subscription Paused',
  SUBSCRIPTION_RESUMED: 'Subscription Resumed',
  COINS_EARNED: 'Coins Earned',
  COINS_REDEEMED: 'Coins Redeemed',
  REFERRAL_SENT: 'Referral Sent',
  REFERRAL_COMPLETED: 'Referral Completed'
};

/**
 * CENTRALIZED ANALYTICS ABSTRACTION LAYER
 *
 * Single point of entry for all analytics and tracking calls
 * Handles deduplication, error handling, and consistent data formatting
 * Eliminates scattered tracking code throughout the application
 */
class AnalyticsService {
  constructor() {
    this.trackedEvents = new Set();
    this.sessionId = sessionStorage.getItem('analytics_session') ||
                     this.generateSessionId();
    sessionStorage.setItem('analytics_session', this.sessionId);
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if event has already been tracked to prevent duplicates
   */
  isEventTracked(eventId) {
    return this.trackedEvents.has(eventId);
  }

  /**
   * Mark event as tracked
   */
  markEventTracked(eventId) {
    this.trackedEvents.add(eventId);
  }

  /**
   * Track page view events
   */
  trackPageView(pageName, pageData = {}) {
    try {
      const eventId = `pageview_${pageName}_${Date.now()}`;

      if (this.isEventTracked(eventId)) return;
      this.markEventTracked(eventId);

      // PostHog
      if (posthog) {
        posthog.capture(POSTHOG_EVENTS.PAGE_VIEW, {
          page_name: pageName,
          session_id: this.sessionId,
          ...pageData,
          timestamp: new Date().toISOString(),
          source: 'web'
        });
      }

      // GTM
      GTM.trackPageView(pageName, window.location.pathname);

      console.log(`📊 Page View: ${pageName}`);
    } catch (error) {
      console.error('Page view tracking error:', error);
    }
  }

  /**
   * Track checkout initiation
   */
  trackCheckoutInitiated(cartData, userData = {}) {
    const cartId = cartData?.id || cartData?.bundle_id || 'unknown';
    const eventId = `checkout_initiated_${cartId}_${Date.now()}`;

    if (this.isEventTracked(eventId)) {
      console.log('Checkout initiation already tracked');
      return;
    }

    try {
      this.markEventTracked(eventId);

      const cartValue = cartData?.cost ||                    // Individual item cost
                       cartData?.total_amount ||             // Total cart value  
                       cartData?.subscription_plan?.cost ||  // Plan cost
                       cartData?.final_payment_amount?.no_coin_and_no_money || 0;
      const currencyType = cartData?.currency_type || 'INR';
      const itemCount = Array.isArray(cartData) ? cartData.length : 1;

      // Facebook Pixel
      if (window.fbq) {
        window.fbq('track', FACEBOOK_EVENTS.INITIATE_CHECKOUT, {
          value: cartValue,
          currency: currencyType,
          content_type: 'product',
          num_items: itemCount
        });
      }

      // Facebook CAPI
      facebookCAPI.trackInitiateCheckout(cartData, userData).catch(err =>
        console.error('CAPI InitiateCheckout error:', err));

      // Google Analytics
      if (window.gtag) {
        window.gtag('event', GOOGLE_ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
          currency: currencyType,
          value: cartValue,
          items: Array.isArray(cartData) ? cartData.map(item => ({
            item_id: item.subscription_plan?.subscription_plan_id,
            item_name: item.subscription_plan?.plan_name,
            price: item.cost || 0,
            quantity: 1
          })) : [{
            item_id: cartData?.bundle_id || 'bundle',
            item_name: cartData?.name || 'Bundle',
            price: cartValue,
            quantity: 1
          }]
        });

        // Google Ads Conversion
        window.gtag('event', GOOGLE_ANALYTICS_EVENTS.CONVERSION, {
          'send_to': GOOGLE_ADS_CONVERSIONS.BEGIN_CHECKOUT,
          'value': cartValue,
          'currency': currencyType
        });
      }

      // Snapchat
      SnapchatPixel.trackStartCheckout({
        currency_type: currencyType,
        total_amount: cartValue,
        items: cartData
      });

      // GTM
      GTM.trackBeginCheckout({
        currency_type: currencyType,
        total_amount: cartValue,
        items: cartData
      });

      // Amplitude
      amplitude.track(AMPLITUDE_EVENTS.CHECKOUT_PAGE_VIEWED, {
        cart_value: cartValue,
        currency: currencyType,
        num_items: itemCount,
        bundle_id: cartData?.bundle_id || null,
        page_url: window.location.href,
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        source: 'web'
      });

      console.log(`🛒 Checkout Initiated: ₹${cartValue} (${itemCount} items)`);
    } catch (error) {
      console.error('Checkout initiation tracking error:', error);
    }
  }

  /**
   * Format phone number to E.164 format
   * @param {string} phone - Phone number to format
   * @returns {string} - E.164 formatted phone number or empty string
   */
  formatPhoneToE164(phone) {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // If it already starts with country code (10+ digits), add + prefix
    if (digits.length >= 10) {
      // If it's exactly 10 digits, assume it's Indian number and add +91
      if (digits.length === 10) {
        return `+91${digits}`;
      }
      // If it's 11+ digits and doesn't start with +, add + prefix
      if (!phone.startsWith('+')) {
        return `+${digits}`;
      }
    }
    
    return phone.startsWith('+') ? phone : `+${digits}`;
  }

  /**
   * Calculate total margin from order items (only checks margin field)
   * @param {Object} orderData - Order data containing new_ordered_items
   * @returns {number} - Total margin amount
   */
  calculateTotalMargin(orderData) {
    return orderData?.new_ordered_items?.reduce((sum, item) => {
      // Only check margin field, ignore calculated_margin and other fields
      const itemMargin = item.subscription_plan?.margin !== null && item.subscription_plan?.margin !== undefined
        ? parseFloat(item.subscription_plan?.margin || 0)
        : 0;
      return sum + itemMargin;
    }, 0) || 0;
  }

  /**
   * Track purchase completion
   */
  trackPurchaseCompleted(orderData, userData = {}) {
    const orderId = orderData?.order_id || orderData?.id;
    const eventId = `purchase_completed_${orderId}`;

    if (this.isEventTracked(eventId)) {
      console.log('Purchase already tracked:', orderId);
      return;
    }

    // Check if any plan name contains "free" or "trial" (case insensitive)
    const hasFreeOrTrialPlan = orderData?.new_ordered_items?.some(item => {
      const planName = item?.subscription_plan?.plan_name?.toLowerCase() || '';
      return planName.includes('free') || planName.includes('trial');
    });

    if (hasFreeOrTrialPlan) {
      console.log('Skipping purchase tracking for free/trial plan:', orderId);
      return;
    }

    try {
      this.markEventTracked(eventId);

      const orderValue = orderData?.payment?.amount ||        // Use actual charged amount
                        orderData?.total_amount || 0;         // Fallback to product value
      const currencyType = orderData?.details?.currency_type ||
                          orderData?.currency_type || 'INR';

      // Facebook Pixel
      if (window.fbq) {
        window.fbq('track', FACEBOOK_EVENTS.PURCHASE, {
          value: orderValue,
          currency: currencyType,
          content_type: 'product',
          content_ids: orderData?.new_ordered_items?.map(item =>
            item.subscription_plan?.subscription_plan_id) || [],
          num_items: orderData?.new_ordered_items?.length || 0,
          order_id: orderId
        });
      }

      // Facebook CAPI
      facebookCAPI.trackPurchase(orderData, userData).catch(err =>
        console.error('CAPI Purchase error:', err));

      // Calculate total margin using common function
      const totalMargin = this.calculateTotalMargin(orderData);
      
      // Use margin if > 0, otherwise fallback to orderValue
      const valueForGoogle = totalMargin > 0 ? totalMargin : orderValue;

      // Google Analytics - Use margin as value, fallback to orderValue if margin is 0
      if (window.gtag) {
        window.gtag('event', GOOGLE_ANALYTICS_EVENTS.PURCHASE, {
          transaction_id: orderId,
          value: valueForGoogle, // Send margin if > 0, otherwise orderValue
          currency: currencyType,
          items: orderData?.new_ordered_items?.map(item => ({
            item_id: item.subscription_plan?.subscription_plan_id,
            item_name: item.subscription_plan?.plan_name,
            price: item.cost,
            quantity: 1
          })) || [],
          // Add user_data for enhanced conversions
          user_data: {
            email: userData?.email || '',
            phone_number: this.formatPhoneToE164(userData?.phone || '')
          }
        });

        // Google Ads Conversion - with deduplication (also use margin as value)
        const conversionId = `ads_purchase_${orderId}`;
        if (!sessionStorage.getItem(conversionId)) {
          sessionStorage.setItem(conversionId, 'true');
          window.gtag('event', GOOGLE_ANALYTICS_EVENTS.CONVERSION, {
            'send_to': GOOGLE_ADS_CONVERSIONS.PURCHASE,
            'value': valueForGoogle, // Send margin if > 0, otherwise orderValue
            'currency': currencyType,
            'transaction_id': orderId
          });
        }
      }

      // Snapchat
      SnapchatPixel.trackPurchase(orderData);

      // GTM
      GTM.trackPurchase(orderData);

      // PostHog
      if (posthog) {
        posthog.capture(POSTHOG_EVENTS.PURCHASE_COMPLETE, {
          order_id: orderId,
          total_amount: orderValue,
          currency: currencyType,
          payment_status: orderData?.payment?.status,
          products: orderData?.new_ordered_items,
          session_id: this.sessionId
        });
      }

      // Amplitude
      amplitude.track(AMPLITUDE_EVENTS.PURCHASE_COMPLETED, {
        order_id: orderId,
        transaction_id: orderId,
        value: orderValue,
        currency: currencyType,
        items: orderData?.new_ordered_items?.map(item => ({
          item_id: item.subscription_plan?.subscription_plan_id,
          item_name: item.subscription_plan?.plan_name,
          price: item.cost,
          quantity: 1,
          // Only add margin field if it exists and is not null
          ...(item.subscription_plan?.margin !== null && item.subscription_plan?.margin !== undefined && {
            margin: parseFloat(item.subscription_plan?.margin || 0)
          })
        })) || [],
        num_items: orderData?.new_ordered_items?.length || 0,
        payment_method: orderData?.payment?.payment_method || "",
        discount_amount: orderData?.amount_saved_dict?.amount_saved || 0,
        fleek_coins_used: orderData?.details?.coin_value_used || 0,
        bundle_id: orderData?.bundle?.id || null,
        // Only add total_margin if it's > 0
        ...(totalMargin > 0 && { total_margin: totalMargin }),
        page_url: window.location.href,
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        source: 'web'
      });

      console.log(`✅ Purchase Completed: ₹${orderValue} (Order: ${orderId})`);
    } catch (error) {
      console.error('Purchase tracking error:', error);
    }
  }

  /**
   * Track payment events
   */
  trackPaymentEvent(eventType, paymentData, userData = {}) {
    try {
      const paymentId = paymentData?.payment_id || paymentData?.order_id;
      const eventId = `payment_${eventType}_${paymentId}_${Date.now()}`;

      if (this.isEventTracked(eventId)) {
        console.log(`Payment ${eventType} already tracked`);
        return;
      }

      this.markEventTracked(eventId);

      // Facebook Pixel
      if (window.fbq) {
        const fbEventName = eventType === PAYMENT_EVENT_TYPES.SUCCESS ? FACEBOOK_EVENTS.PAYMENT_SUCCESS :
                           eventType === PAYMENT_EVENT_TYPES.FAILED ? FACEBOOK_EVENTS.PAYMENT_FAILED : 
                           FACEBOOK_EVENTS.PAYMENT;
        window.fbq('track', fbEventName, {
          value: paymentData?.amount || 0,
          currency: paymentData?.currency || 'INR',
          payment_id: paymentId,
          order_id: paymentData?.order_id
        });
      }

      // Facebook CAPI
      if (eventType === PAYMENT_EVENT_TYPES.SUCCESS) {
        facebookCAPI.trackPayment(paymentData, userData).catch(err =>
          console.error('CAPI Payment error:', err));
      }

      // Amplitude
      const amplitudeEventName = eventType === PAYMENT_EVENT_TYPES.SUCCESS ? AMPLITUDE_EVENTS.PAYMENT_SUCCESS :
                                eventType === PAYMENT_EVENT_TYPES.FAILED ? AMPLITUDE_EVENTS.PAYMENT_FAILED :
                                eventType === PAYMENT_EVENT_TYPES.PROCESSING ? AMPLITUDE_EVENTS.PAYMENT_PROCESSING :
                                `Payment ${eventType.charAt(0).toUpperCase() + eventType.slice(1)}`;
      
      amplitude.track(amplitudeEventName, {
        payment_id: paymentId,
        order_id: paymentData?.order_id,
        amount: paymentData?.amount || 0,
        currency: paymentData?.currency || 'INR',
        gateway: paymentData?.gateway || 'juspay',
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        source: 'web'
      });

      console.log(`💳 Payment ${eventType}: ₹${paymentData?.amount || 0}`);
    } catch (error) {
      console.error('Payment tracking error:', error);
    }
  }

  /**
   * Track add to cart events
   */
  trackAddToCart(productData, userData = {}) {
    try {
      const productId = productData?.subscription_plan_id || productData?.id;
      const eventId = `add_to_cart_${productId}_${Date.now()}`;

      if (this.isEventTracked(eventId)) {
        console.log('Add to cart already tracked');
        return;
      }

      this.markEventTracked(eventId);

      // Facebook Pixel
      if (window.fbq) {
        window.fbq('track', FACEBOOK_EVENTS.ADD_TO_CART, {
          content_type: 'product',
          content_ids: [productId],
          content_name: productData?.plan_name || productData?.name,
          currency: productData?.currency_type || 'INR',
          value: productData?.discounted_cost || productData?.price || 0
        });
      }

      // Facebook CAPI
      facebookCAPI.trackAddToCart(productData, userData).catch(err =>
        console.error('CAPI AddToCart error:', err));

      // Google Analytics
      if (window.gtag) {
        window.gtag('event', GOOGLE_ANALYTICS_EVENTS.ADD_TO_CART, {
          currency: productData?.currency_type || 'INR',
          value: productData?.discounted_cost || productData?.price || 0,
          items: [{
            item_id: productId,
            item_name: productData?.plan_name || productData?.name,
            price: productData?.discounted_cost || productData?.price || 0,
            quantity: 1
          }]
        });

        // Google Ads Conversion
        window.gtag('event', GOOGLE_ANALYTICS_EVENTS.CONVERSION, {
          'send_to': GOOGLE_ADS_CONVERSIONS.ADD_TO_CART,
          'value': productData?.discounted_cost || productData?.price || 0,
          'currency': productData?.currency_type || 'INR'
        });
      }

      SnapchatPixel.trackAddToCart(productData);

      GTM.trackAddToCart(productData);

      // PostHog
      if (posthog) {
        posthog.capture(POSTHOG_EVENTS.ADD_TO_CART, {
          product_id: productId,
          product_name: productData?.plan_name || productData?.name,
          price: productData?.price,
          currency: productData?.currency_type,
          session_id: this.sessionId
        });
      }

      console.log(`🛍️ Add to Cart: ${productData?.plan_name || productData?.name}`);
    } catch (error) {
      console.error('Add to cart tracking error:', error);
    }
  }

  /**
   * Track view content events (product/plan views)
   */
  trackViewContent(contentData, userData = {}) {
    try {
      const contentId = contentData?.subscription_plan_id || contentData?.id || contentData?.content_ids?.[0];
      const eventId = `view_content_${contentId}_${Date.now()}`;

      if (this.isEventTracked(eventId)) {
        console.log('View content already tracked');
        return;
      }

      this.markEventTracked(eventId);

      const contentIds = contentData?.content_ids || [contentId];
      const contentType = contentData?.content_type || 'product';
      const contentName = contentData?.content_name || contentData?.plan_name || contentData?.name || '';
      const contentValue = contentData?.value || contentData?.discounted_cost || contentData?.price || 0;
      const currency = contentData?.currency || contentData?.currency_type || 'INR';

      // Facebook Pixel
      if (window.fbq) {
        window.fbq('track', FACEBOOK_EVENTS.VIEW_CONTENT, {
          content_ids: contentIds,
          content_type: contentType,
          content_name: contentName,
          currency: currency,
          value: contentValue
        });
      }

      // // Google Analytics
      // if (window.gtag) {
      //   window.gtag('event', 'view_item', {
      //     currency: currency,
      //     value: contentValue,
      //     items: [{
      //       item_id: contentId,
      //       item_name: contentName,
      //       price: contentValue
      //     }]
      //   });
      // }

      console.log(`👁️ View Content: ${contentName} (${contentType})`);
    } catch (error) {
      console.error('View content tracking error:', error);
    }
  }

  /**
   * Track user authentication events
   */
  trackAuthEvent(eventType, userData = {}) {
    try {
      const eventId = `auth_${eventType}_${Date.now()}`;

      if (this.isEventTracked(eventId)) return;
      this.markEventTracked(eventId);

      // Amplitude
      const amplitudeAuthEvent = eventType === AUTH_EVENT_TYPES.LOGIN ? AMPLITUDE_EVENTS.LOGIN_SUCCESS :
                                 eventType === AUTH_EVENT_TYPES.SIGNUP ? AMPLITUDE_EVENTS.SIGNUP_SUCCESS :
                                 `${eventType} Success`;
      
      amplitude.track(amplitudeAuthEvent, {
        phone: userData?.phone,
        method: userData?.method || 'phone_otp',
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        source: 'web'
      });

      // Facebook Pixel
      if (window.fbq && eventType === AUTH_EVENT_TYPES.LOGIN) {
        window.fbq('trackCustom', FACEBOOK_EVENTS.CUSTOM_LOGIN, {
          method: userData?.method || 'phone_otp',
          success: true
        });
      }

      // Snapchat
      if (eventType === AUTH_EVENT_TYPES.LOGIN) {
        SnapchatPixel.trackLogin(userData);
      }

      console.log(`🔐 ${eventType}: ${userData?.phone || 'Anonymous'}`);
    } catch (error) {
      console.error('Auth tracking error:', error);
    }
  }

  /**
   * Track custom events with provider-specific targeting
   * @param {string} eventName - Name of the custom event
   * @param {Object} eventData - Event data/properties
   * @param {string|string[]|null} providers - Target providers (single, array, or null for all)
   *   - null/undefined: Send to all providers (default)
   *   - string: Send to single provider (e.g., ANALYTICS_PROVIDERS.AMPLITUDE)
   *   - array: Send to multiple providers (e.g., [ANALYTICS_PROVIDERS.AMPLITUDE, ANALYTICS_PROVIDERS.POSTHOG])
   * 
   * @example
   * // Send to all providers (default)
   * trackCustomEvent(CUSTOM_EVENTS.BUTTON_CLICKED, { button_name: 'CTA' });
   * 
   * // Send to Amplitude only
   * trackCustomEvent(CUSTOM_EVENTS.API_RESPONSE_TIME, { value: 123, endpoint: '/api/orders' }, ANALYTICS_PROVIDERS.AMPLITUDE);
   * 
   * // Send to specific providers
   * trackCustomEvent(CUSTOM_EVENTS.AB_TEST_IMPRESSION, { variant: 'A', test_name: 'checkout_v2' }, [ANALYTICS_PROVIDERS.AMPLITUDE, ANALYTICS_PROVIDERS.POSTHOG]);
   */
  trackCustomEvent(eventName, eventData = {}, providers = null) {
    try {
      const eventId = `custom_${eventName}_${Date.now()}`;

      if (this.isEventTracked(eventId)) return;
      this.markEventTracked(eventId);

      // Normalize providers to array
      let targetProviders = [];
      if (providers === null || providers === undefined) {
        // Send to all providers
        targetProviders = Object.values(ANALYTICS_PROVIDERS);
      } else if (Array.isArray(providers)) {
        targetProviders = providers;
      } else if (typeof providers === 'string') {
        targetProviders = [providers];
      }

      // Facebook Pixel
      if (targetProviders.includes(ANALYTICS_PROVIDERS.FACEBOOK_PIXEL) && window.fbq) {
        window.fbq('trackCustom', eventName, {
          ...eventData,
          timestamp: new Date().toISOString()
        });
      }

      // Facebook CAPI
      if (targetProviders.includes(ANALYTICS_PROVIDERS.FACEBOOK_CAPI)) {
        facebookCAPI.sendEvent(eventName, eventData.userData || {}, {
          ...eventData,
          event_source: 'web'
        }).catch(err => console.error('CAPI Custom Event error:', err));
      }

      // Google Analytics
      if (targetProviders.includes(ANALYTICS_PROVIDERS.GOOGLE_ANALYTICS) && window.gtag) {
        window.gtag('event', eventName.toLowerCase().replace(/\s+/g, '_'), {
          custom_parameter: JSON.stringify(eventData),
          event_category: 'custom',
          event_label: eventName,
          ...eventData
        });
      }

      // Google Ads (Custom Conversion)
      if (targetProviders.includes(ANALYTICS_PROVIDERS.GOOGLE_ADS) && window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-10792886696/custom-event-conversion-label',
          'custom_parameter': eventName,
          'value': eventData.value || 0,
          'currency': eventData.currency || 'INR'
        });
      }

      // Snapchat
      if (targetProviders.includes(ANALYTICS_PROVIDERS.SNAPCHAT)) {
        SnapchatPixel.trackEvent(eventName.toUpperCase().replace(/\s+/g, '_'), {
          ...eventData,
          custom_event: true
        }).catch(err => console.error('Snapchat Custom Event error:', err));
      }

      // PostHog
      if (targetProviders.includes(ANALYTICS_PROVIDERS.POSTHOG) && posthog) {
        posthog.capture(eventName, {
          ...eventData,
          session_id: this.sessionId,
          custom_event: true
        });
      }

      // Amplitude
      if (targetProviders.includes(ANALYTICS_PROVIDERS.AMPLITUDE)) {
        amplitude.track(eventName, {
          ...eventData,
          session_id: this.sessionId,
          timestamp: new Date().toISOString(),
          source: 'web',
          custom_event: true
        });
      }

      // GTM
      if (targetProviders.includes(ANALYTICS_PROVIDERS.GTM)) {
        GTM.trackCustomEvent?.(eventName, eventData) || 
        window.dataLayer?.push({
          event: 'custom_event',
          event_name: eventName,
          ...eventData
        });
      }

      const providerList = targetProviders.length === Object.values(ANALYTICS_PROVIDERS).length 
        ? 'all providers' 
        : targetProviders.join(', ');
      
      console.log(`📈 Custom Event: ${eventName} → ${providerList}`);
    } catch (error) {
      console.error('Custom event tracking error:', error);
    }
  }

  /**
   * Track error events
   */
  trackError(error, context = {}) {
    try {
      const eventId = `error_${Date.now()}`;

      if (this.isEventTracked(eventId)) return;
      this.markEventTracked(eventId);

      // PostHog
      if (posthog) {
        posthog.capture(POSTHOG_EVENTS.ERROR_OCCURRED, {
          error_message: error.message,
          error_type: error.name,
          error_context: context,
          page_url: window.location.href,
          session_id: this.sessionId
        });
      }

      // Amplitude
      amplitude.track(AMPLITUDE_EVENTS.ERROR_OCCURRED, {
        error_message: error.message,
        error_type: error.name,
        error_context: context,
        page_url: window.location.href,
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        source: 'web'
      });

      console.error('🚨 Error Tracked:', error.message);
    } catch (trackError) {
      console.error('Error tracking failed:', trackError);
    }
  }

  /**
   * Clear tracking history (useful for testing)
   */
  clearTrackingHistory() {
    this.trackedEvents.clear();
    console.log('🧹 Tracking history cleared');
  }

  /**
   * Get tracking statistics
   */
  getTrackingStats() {
    return {
      totalEventsTracked: this.trackedEvents.size,
      sessionId: this.sessionId,
      events: Array.from(this.trackedEvents)
    };
  }
}

// Singleton instance
const analyticsService = new AnalyticsService();

// Export the service instance
export default analyticsService;

// Export bound methods to preserve 'this' context
export const trackPageView = analyticsService.trackPageView.bind(analyticsService);
export const trackCheckoutInitiated = analyticsService.trackCheckoutInitiated.bind(analyticsService);
export const trackPurchaseCompleted = analyticsService.trackPurchaseCompleted.bind(analyticsService);
export const trackPaymentEvent = analyticsService.trackPaymentEvent.bind(analyticsService);
export const trackAddToCart = analyticsService.trackAddToCart.bind(analyticsService);
export const trackViewContent = analyticsService.trackViewContent.bind(analyticsService);
export const trackAuthEvent = analyticsService.trackAuthEvent.bind(analyticsService);
export const trackCustomEvent = analyticsService.trackCustomEvent.bind(analyticsService);
export const trackError = analyticsService.trackError.bind(analyticsService);
